const DEFAULT_GYEONGGI_TENANT_SLUG = 'ihfb-dev';

export const GYEONGGI_TENANT_SLUG_COOKIE = 'x-tenant-slug';
export const GYEONGGI_SUPPORTED_LOCALES = ['ko', 'en', 'zh-CN', 'zh-TW', 'vi', 'ru', 'ja'] as const;

const GYEONGGI_TENANT_ROUTE_LOCALES = [...GYEONGGI_SUPPORTED_LOCALES, 'es-SV'] as const;

export const GYEONGGI_TENANT_RESERVED_ROUTE_SEGMENTS = [
  'account',
  'admin',
  'api',
  'auth',
  'boards',
  'certificate',
  'cms',
  'consent',
  'courses',
  'curriculum',
  ...GYEONGGI_TENANT_ROUTE_LOCALES,
  'fonts',
  'group',
  'instructor',
  'introduction',
  'live',
  'login',
  'meta-content',
  'my-info',
  'my-learning',
  'pages',
  'posts',
  'privacy',
  's',
  'search',
  'signup',
  'surveys',
  'te',
  'tenant',
  'terms',
  'trtc-assets',
  'user',
  'v',
  'web-content',
] as const;

const reservedRouteSegmentSet = new Set<string>(GYEONGGI_TENANT_RESERVED_ROUTE_SEGMENTS);
const tenantRouteLocaleSet = new Set<string>(GYEONGGI_TENANT_ROUTE_LOCALES);

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getPathSegments = (pathname: string) =>
  splitPathnameSuffix(pathname).pathname.split('/').filter(Boolean);

export function getDefaultGyeonggiTenantSlug(env?: Record<string, string | undefined>) {
  const resolvedEnv = env ?? (typeof process === 'undefined' ? undefined : process.env);

  return resolvedEnv?.NEXT_PUBLIC_DEFAULT_SUBDOMAIN?.trim() || DEFAULT_GYEONGGI_TENANT_SLUG;
}

const splitPathnameSuffix = (pathnameOrUrl: string) => {
  const queryIndex = pathnameOrUrl.indexOf('?');
  const hashIndex = pathnameOrUrl.indexOf('#');
  const suffixIndex = [queryIndex, hashIndex].filter((index) => index >= 0).sort((a, b) => a - b)[0];

  if (suffixIndex === undefined) {
    return { pathname: pathnameOrUrl, suffix: '' };
  }

  return {
    pathname: pathnameOrUrl.slice(0, suffixIndex),
    suffix: pathnameOrUrl.slice(suffixIndex),
  };
};

const isInternalPath = (href: string) => href.startsWith('/') && !href.startsWith('//');

const removeTrailingSlash = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

const normalizeGyeonggiInternalPathname = (pathname: string) => {
  if (pathname === '/group' || pathname.startsWith('/group/')) {
    return `/s${pathname}`;
  }

  return pathname;
};

const getTenantComparablePathname = (pathnameOrUrl: string) => {
  const { pathname } = splitPathnameSuffix(pathnameOrUrl);
  return removeTrailingSlash(stripGyeonggiTenantPathPrefix(pathname));
};

export function isGyeonggiTenantReservedRouteSegment(segment?: string | null) {
  return !segment || reservedRouteSegmentSet.has(segment);
}

export function getGyeonggiTenantSlugFromPathname(pathname: string) {
  const [rawFirstSegment, secondSegment, rawRewriteTenantSegment] = getPathSegments(pathname);

  if (rawFirstSegment && tenantRouteLocaleSet.has(rawFirstSegment) && secondSegment === 's') {
    return rawRewriteTenantSegment ? safeDecodeURIComponent(rawRewriteTenantSegment) : null;
  }

  const firstSegment = rawFirstSegment ? safeDecodeURIComponent(rawFirstSegment) : undefined;

  if (isGyeonggiTenantReservedRouteSegment(firstSegment)) {
    return null;
  }

  return firstSegment ?? null;
}

export function stripGyeonggiTenantPathPrefix(pathname: string) {
  const segments = getPathSegments(pathname);
  const [rawFirstSegment, ...restSegments] = segments;

  if (rawFirstSegment && tenantRouteLocaleSet.has(rawFirstSegment) && restSegments[0] === 's') {
    const rewriteRestSegments = segments.slice(3);
    return rewriteRestSegments.length > 0 ? `/${rewriteRestSegments.join('/')}` : '/';
  }

  const firstSegment = rawFirstSegment ? safeDecodeURIComponent(rawFirstSegment) : undefined;

  if (isGyeonggiTenantReservedRouteSegment(firstSegment)) {
    return pathname || '/';
  }

  return restSegments.length > 0 ? `/${restSegments.join('/')}` : '/';
}

export function stripGyeonggiTenantPathPrefixFromUrl(pathnameOrUrl: string) {
  if (!isInternalPath(pathnameOrUrl)) {
    return pathnameOrUrl;
  }

  const { pathname, suffix } = splitPathnameSuffix(pathnameOrUrl);
  return `${stripGyeonggiTenantPathPrefix(pathname)}${suffix}`;
}

export function stripCurrentGyeonggiTenantPathPrefixFromUrl(
  pathnameOrUrl: string,
  tenantSlug = getCurrentGyeonggiTenantSlug(),
) {
  if (!isInternalPath(pathnameOrUrl)) {
    return pathnameOrUrl;
  }

  const { pathname, suffix } = splitPathnameSuffix(pathnameOrUrl);

  if (getGyeonggiTenantSlugFromPathname(pathname) !== tenantSlug) {
    return pathnameOrUrl;
  }

  return `${stripGyeonggiTenantPathPrefix(pathname)}${suffix}`;
}

export function getCurrentGyeonggiTenantSlug(pathname?: string) {
  const currentPathname = pathname ?? (typeof window === 'undefined' ? undefined : window.location.pathname);

  if (!currentPathname) {
    return getDefaultGyeonggiTenantSlug();
  }

  return getGyeonggiTenantSlugFromPathname(currentPathname) ?? getDefaultGyeonggiTenantSlug();
}

export function getGyeonggiTenantPathPrefix(tenantSlug = getCurrentGyeonggiTenantSlug()) {
  return `/${encodeURIComponent(tenantSlug)}`;
}

export function prependGyeonggiTenantPath(pathname: string, tenantSlug = getCurrentGyeonggiTenantSlug()) {
  const normalizedPathname = normalizeGyeonggiInternalPathname(
    pathname.startsWith('/') ? pathname : `/${pathname}`,
  );

  if (normalizedPathname === '/') {
    return getGyeonggiTenantPathPrefix(tenantSlug);
  }

  return `${getGyeonggiTenantPathPrefix(tenantSlug)}${normalizedPathname}`;
}

export function resolveGyeonggiTenantInternalHref(href: string, tenantSlug = getCurrentGyeonggiTenantSlug()) {
  if (!isInternalPath(href)) {
    return href;
  }

  const { pathname, suffix } = splitPathnameSuffix(
    stripCurrentGyeonggiTenantPathPrefixFromUrl(href, tenantSlug),
  );
  return `${prependGyeonggiTenantPath(pathname, tenantSlug)}${suffix}`;
}

export function resolveGyeonggiTenantCmsHref(href: string, tenantSlug = getCurrentGyeonggiTenantSlug()) {
  if (!isInternalPath(href)) {
    return href;
  }

  const { pathname, suffix } = splitPathnameSuffix(stripGyeonggiTenantPathPrefixFromUrl(href));
  const normalizedCmsPathname =
    pathname === '/cms' || pathname.startsWith('/cms/') ? pathname : `/cms${pathname === '/' ? '' : pathname}`;

  return `${prependGyeonggiTenantPath(normalizedCmsPathname, tenantSlug)}${suffix}`;
}

export function isGyeonggiTenantInternalHrefActive(pathname: string, href: string) {
  if (!isInternalPath(href)) {
    return false;
  }

  const comparablePathname = getTenantComparablePathname(pathname);
  const comparableHref = getTenantComparablePathname(href);

  if (comparableHref === '/') {
    return comparablePathname === '/';
  }

  return comparablePathname === comparableHref || comparablePathname.startsWith(`${comparableHref}/`);
}
